import { useState, Fragment, useEffect } from 'react';
import { useRouter } from 'next/router';
import { type AxiosRequestConfig } from 'axios';

import { Button, Col, Row, TextField, Typography } from 'darrius';

import { Header } from '../../components/atoms';

import { Toast, showToast } from '../../components/atoms/Toast/Toast';

import { HeadContent, PIN } from '../../components/molecules';

import { PhoneInUse, ChoiceCategories } from './partials';

import { type UserSignupType, initialState } from '../../types/UserSignup';
import {
  StepByStepData,
  StepByStepDataForMFA,
} from '../../data/signup/stepByStepData';
import { axiosInstance } from '../../utils/axiosInstance';
import { userDataPatterns } from '../../utils/fieldPatterns';

import styles from './SignupTemplate.module.scss';

import {
  addCepMask,
  addCpfOrCnpjMask,
  addDateMask,
  addPhoneMask,
} from '../../utils/parsedMasks';
import { notifyErrorClient } from '../../services/notifyError/client';
import { errorPrefix } from '../../utils/errorPrefix';
import { CreatePassword } from '../RedefinePassword/partials/CreatePassword';
import { clientCookie } from '@/utils/clientCookie';

export default function SignupTemplate() {
  const router = useRouter();

  const StepByStep =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_LOGIN === 'true'
      ? StepByStepDataForMFA
      : StepByStepData;
  const [document, setDocument] = useState(null);

  const [stepNumber, setStepNumber] = useState(0);
  const [currentStep, setCurrentStep] = useState(StepByStep[stepNumber]);
  const [userData, setUserData] = useState<UserSignupType>(initialState);

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [lockPhoneField, setLockPhoneField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fieldErrorMessage, setFieldErrorMessage] = useState({});
  const [phoneInUse, setPhoneInUse] = useState(false);

  const { query } = useRouter();
  const { id, title, description, components, request, triggerTracking } =
    currentStep;

  function getPayloadRequest(fields) {
    let payload = {};
    fields.forEach((field) => {
      if (userData.homeAddressForm) {
        if (field === 'cep') {
          payload = { ...payload, [field]: userData.home_cep };
        } else if (field === 'home_cep') {
          payload = { ...payload, [field]: userData.cep };
        } else {
          payload = { ...payload, [field]: userData[field] };
        }
      } else {
        if (field !== 'home_cep') {
          payload = { ...payload, [field]: userData[field] };
        }
      }
    });
    return payload;
  }

  async function executeRequest(requestParam) {
    setIsLoading(true);
    const { path, fields, method = 'GET' } = requestParam;

    if (id === 'PHONE') {
      return axiosInstance({
        url: `${path}?phone_number=${userData.phone}`,
        method,
      });
    }

    const requestOptions: AxiosRequestConfig = {
      url: `${path}`,
      method,
      data: getPayloadRequest(fields),
    };

    const result = await axiosInstance(requestOptions);
    return result.data;
  }

  async function handleNextStep(requestParam) {
    if (id === 'CATEGORY') setLockPhoneField(true);

    try {
      if (requestParam) {
        const { token, redirectUrl, homeAddressForm } =
          await executeRequest(requestParam);
        setUserData((oldState) => ({
          ...oldState,
          token: !oldState.token ? token : oldState.token,
          redirectURL:
            id === 'E-MAIL' ? `${redirectUrl}` : oldState.redirectURL,
          homeAddressForm:
            id === 'E-MAIL' ? homeAddressForm : oldState.homeAddressForm,
        }));
      }

      triggerTracking({ ...userData });
      setStepNumber((oldState) => oldState + 1);
      setCurrentStep(StepByStep[stepNumber + 1]);
    } catch (error) {
      if (id === 'PHONE' && error.response.status === 400) {
        setPhoneInUse(true);
        setIsLoading(false);
        return;
      }

      if (!error.response.data.clientMessage) {
        showToast({
          message:
            'Estamos com dificuldades técnicas, tente novamente mais tarde!',
          type: 'error',
        });
      } else {
        showToast({
          message: error.response.data.clientMessage,
          type: 'error',
        });
      }

      notifyErrorClient(errorPrefix('[SIGNUP-TEMPLATE][NEXT-STEP]', error));

      setIsLoading(false);
    }
  }

  function handleBackStep() {
    if (id === 'CODE_VALIDATION') {
      triggerTracking({ action: 'change_number', token: userData.token });
      setLockPhoneField(false);
    }

    setStepNumber((oldState) => oldState - 1);
    setCurrentStep(StepByStep[stepNumber - 1]);
  }

  function handleValidateFields(value, field) {
    if (!value) {
      return 'Campo obrigatório';
    }

    if (
      userDataPatterns[field] &&
      !new RegExp(userDataPatterns[field].regexp).test(value)
    ) {
      return userDataPatterns[field].error;
    }
  }

  function validateDocument(value) {
    const isDocumentCPF = new RegExp(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/).test(
      value
    );
    const isDocumentCNPJ = new RegExp(
      /^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/
    ).test(value);

    if (isDocumentCPF) {
      setDocument('CPF');
    } else if (isDocumentCNPJ) {
      setDocument('CNPJ');
    } else {
      setDocument(null);
    }
  }

  function handleChangeValueForm(value, field) {
    const errorMessage = handleValidateFields(value, field);

    if (errorMessage) {
      setFieldErrorMessage({ [field]: errorMessage });
    } else {
      setFieldErrorMessage({});
    }

    if (id === 'PERSONAL_INFO' || id === 'DOCUMENT') {
      triggerTracking({ errorMessage, token: userData.token });
    }

    if (field === 'cpfOrCnpj') {
      validateDocument(value);
    }

    setUserData((oldState) => ({
      ...oldState,
      [field]: value,
      phone: field === 'phone' ? addPhoneMask(value) : oldState.phone,
      cep: field === 'cep' ? addCepMask(value) : oldState.cep,
      cpfOrCnpj:
        field === 'cpfOrCnpj' ? addCpfOrCnpjMask(value) : oldState.cpfOrCnpj,
      birthday: field === 'birthday' ? addDateMask(value) : oldState.birthday,
      founded_at:
        field === 'founded_at' ? addDateMask(value) : oldState.founded_at,
      home_cep: oldState.checkCep
        ? oldState.cep
        : field === 'home_cep'
          ? addCepMask(value)
          : oldState.home_cep,
    }));

    setIsButtonDisabled(false);
  }

  const handleSendGclid = async (userData: UserSignupType): Promise<void> => {
    let url = router.asPath;

    const cookieUrl = clientCookie.get('router_url');
    const title = clientCookie.get('selectedCategory');

    if (cookieUrl) {
      url = cookieUrl;
    }

    const g_clid_url = encodeURI(url);
    const gclidFromCookie = clientCookie.get('gclid');
    const gclid = router?.query?.gclid || gclidFromCookie;

    const requestOptions: AxiosRequestConfig = {
      url: `${process.env.NEXT_PUBLIC_API_PRO}/google/google-events`,
      method: 'POST',
      data: {
        g_clid_url: g_clid_url,
        gclid: gclid || '',
        email: userData.email,
        cpf: userData.cpfOrCnpj,
        title,
      },
    };

    await axiosInstance(requestOptions);

    if (gclidFromCookie) {
      clientCookie.erase('gclid');
    }

    return;
  };

  async function handleSendForm(requestParam) {
    try {
      const components = currentStep.components
        .slice(0, currentStep.components.length - 2)
        .filter((c) => !c.condition || c.condition === document);

      let invalid;

      components.forEach((c) => {
        if (!userData[c.name] && !invalid) {
          invalid = c.name;
        }
      });

      if (invalid) {
        setFieldErrorMessage((oldState) => ({
          ...oldState,
          [invalid]: `Este campo é requerido`,
        }));

        return;
      }

      if (!userData.allowLGPD) {
        setFieldErrorMessage((oldState) => ({
          ...oldState,
          lgpd: 'Este campo é requerido',
        }));
        return;
      }

      await executeRequest(requestParam);

      handleSendGclid(userData);

      window.location.href = '/seja-um-profissional/cadastro/sucesso';
    } catch (error) {
      notifyErrorClient(
        errorPrefix('[SIGNUP-TEMPLATE][HANDLE-SEND-FORM]', error)
      );

      console.log('error', error);

      showToast({ message: error.response.data.clientMessage, type: 'error' });

      setIsLoading(false);
    }
  }

  function checkCep() {
    setUserData((oldState) => ({
      ...oldState,
      checkCep: !oldState.checkCep,
    }));

    if (userData.checkCep) {
      setUserData((oldState) => ({
        ...oldState,
        home_cep: '',
      }));
    } else {
      setUserData((oldState) => ({
        ...oldState,
        home_cep: oldState.cep,
      }));
    }
  }

  function hasDisabledButton() {
    const fieldsWithError = Object.keys(fieldErrorMessage).filter(
      (field) => field
    );
    const hasInvalidValue = fieldsWithError.length > 0;
    return hasInvalidValue || isButtonDisabled || isLoading;
  }

  function mountBodyForm(componentsParam, requestParam) {
    return componentsParam.map((component, index) => {
      if (component.condition && component.condition !== document) {
        return;
      }

      if (component.type === 'textfield') {
        const field = component.name;

        return (
          <Fragment key={index}>
            <TextField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              onChange={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              onBlur={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
            />
          </Fragment>
        );
      }

      if (component.type === 'textfield_cep' && !userData.checkCep) {
        const field = component.name;

        return (
          <Fragment key={index}>
            <TextField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              onChange={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              onBlur={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
            />
          </Fragment>
        );
      }

      if (component.type === 'textfield_phone') {
        const field = component.name;

        return (
          <Fragment key={index}>
            <TextField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              onChange={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              onBlur={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
              disabled={lockPhoneField}
              // autoFocus
            />
          </Fragment>
        );
      }

      if (component.type === 'datefield') {
        const field = component.name;

        return (
          <Fragment key={index}>
            <TextField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              onChange={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              onBlur={(event) =>
                handleChangeValueForm(event.target.value, field)
              }
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
              type="tel"
              minLength={10}
              maxLength={10}
            />
          </Fragment>
        );
      }

      if (component.type === 'selectfield') {
        const field = component.name;

        return (
          <Fragment key={index}>
            <div>
              <label htmlFor={id} className="text-field__label">
                {component.label}
              </label>

              <div className="container-input">
                <select
                  className={`text-field-container ${styles.select}`}
                  id={component.name}
                  name={component.name}
                  onChange={(event) =>
                    handleChangeValueForm(event.target.value, field)
                  }
                  onBlur={(event) =>
                    handleChangeValueForm(event.target.value, field)
                  }
                >
                  <option disabled selected value="">
                    Selecione uma opção
                  </option>

                  {component.options.map((option) => (
                    <option>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </Fragment>
        );
      }

      if (component.type === 'button') {
        return (
          <Button.Root
            key={index}
            className={styles.signup__button}
            fullWidth
            onClick={async () => handleNextStep(requestParam)}
            disabled={hasDisabledButton()}
          >
            {!isLoading ? component.label : <>Carregando</>}
          </Button.Root>
        );
      }

      if (component.type === 'end_button') {
        return (
          <Button.Root
            key={index}
            className="signup__button"
            fullWidth
            onClick={async () => handleSendForm(requestParam)}
            disabled={hasDisabledButton()}
          >
            {!isLoading ? component.label : <>Carregando</>}
          </Button.Root>
        );
      }

      if (component.type === 'password') {
        return (
          <CreatePassword
            onButtonClick={async (password) => {
              setUserData((oldState) => ({
                ...oldState,
                password,
              }));
              handleNextStep(requestParam);
            }}
            isButtonLoading={false}
          />
        );
      }

      if (component.type === 'categories') {
        return (
          <Fragment key={index}>
            <ChoiceCategories
              updateUserData={setUserData}
              userData={userData}
            />
          </Fragment>
        );
      }

      if (component.type === 'pin') {
        return (
          <div className={styles['signup__pin-code']} key={index}>
            <img
              src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/phone-validation.svg`}
              alt="Imagem de um telefone celular"
            />
            <Typography.Title size="3xl" className="space-top">
              Digite o código de 4 dígitos para verificar seu celular
            </Typography.Title>
            <Typography.Text
              className={`${styles.text} space-bottom`}
            >{`Enviado para o número ${userData.phone}`}</Typography.Text>
            <PIN numberOfDigits={4} handlePinValue={setUserData} />
            <Button.Root
              variation="clear"
              size="small"
              onClick={handleBackStep}
            >
              Mudar número
            </Button.Root>
          </div>
        );
      }

      if (component.type === 'checkbox_lgpd') {
        const { label, cta, name } = component;

        return (
          <>
            <div className={styles['signup__checkbox-lgpd']} key={index}>
              <input
                type="checkbox"
                required
                checked={userData.allowLGPD}
                onChange={() =>
                  setUserData((oldState) => ({
                    ...oldState,
                    allowLGPD: !oldState.allowLGPD,
                  }))
                }
              />
              <span>
                {label}
                <a target="_blank" href={cta.link} rel="noreferrer">
                  {cta.text}
                </a>
              </span>
            </div>
            <span className={styles['signup__checkbox__error-message']}>
              {fieldErrorMessage[name]}
            </span>
          </>
        );
      }

      if (userData.homeAddressForm && component.type === 'checkbox_cep') {
        const { label } = component;
        const field = component.name;

        return (
          <>
            <div className={styles['signup__checkbox-cep']} key={index}>
              <input
                type="checkbox"
                required
                checked={userData.checkCep}
                onChange={() => {
                  checkCep();
                }}
              />
              <span>{label}</span>
            </div>
          </>
        );
      }
    });
  }

  useEffect(() => {
    if (query['no-pre_signup']) {
      const sessionData = sessionStorage.getItem(
        '@site_profissionais:pre_signup'
      );
      const parsedData = JSON.parse(sessionData);

      setUserData((oldSate) => ({
        ...oldSate,
        email: parsedData.email,
        phone: parsedData.phone,
        redirectURL: parsedData.redirectURL,
        token: parsedData.token,
        homeAddressForm: parsedData.homeAddressForm,
      }));

      const categoriesStep = 2;
      setStepNumber(categoriesStep);
      setCurrentStep(StepByStep[categoriesStep]);
    }

    if (query['test']) {
      setCurrentStep(StepByStep[StepByStep.length - 1]);
    }
  }, [query]);

  useEffect(() => {
    setIsLoading(false);
    setIsButtonDisabled(true);
  }, [stepNumber]);

  useEffect(() => {
    const validatePin = async () => {
      try {
        await executeRequest(request);
        triggerTracking({ action: 'insert_number', token: userData.token });

        setStepNumber((oldState) => oldState + 1);
        setCurrentStep(StepByStep[stepNumber + 1]);
      } catch (error) {
        notifyErrorClient(
          errorPrefix('[SIGNUP-TEMPLATE][VALIDATE-PIN]', error)
        );

        showToast({
          message: error.response.data.clientMessage,
          type: 'error',
        });
        setIsLoading(false);
      }
    };

    if (userData.pin.length === 4) validatePin();
  }, [userData.pin]);

  useEffect(() => {
    if (userData.subCategoryIds.length === 0) setIsButtonDisabled(true);
    if (userData.subCategoryIds.length > 0) setIsButtonDisabled(false);
  }, [userData.subCategoryIds]);

  useEffect(() => {
    if (userData.allowLGPD) {
      setIsButtonDisabled(false);
      setFieldErrorMessage({});
    }
  }, [userData.allowLGPD]);

  return (
    <>
      <HeadContent
        metaTitle="GetNinjas | Orçamento de Profissionais Confiáveis no GetNinjas.com.br"
        slug="cadastro web"
        metaDescription=""
        type="prospect:signup-web"
      />

      <Header.Simple />

      <div className={`signup ${styles.signup}`}>
        {phoneInUse ? (
          <PhoneInUse phone={userData.phone} setPhoneInUse={setPhoneInUse} />
        ) : (
          <>
            {stepNumber !== 0 && (
              <div className={styles['signup__container__back-button']}>
                <button type="button" onClick={handleBackStep}>
                  <img
                    src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/back-arrow.svg`}
                    alt="Seta para volta ao passo anterior"
                  />
                </button>
              </div>
            )}

            {title && (
              <Row className="center-md space-bottom space-top">
                <Col className="col-sm-12 col-md-6">
                  <Typography.Title size="3xl" as="h3">
                    {title}
                  </Typography.Title>
                  <Typography.Text>{description}</Typography.Text>
                </Col>
              </Row>
            )}

            <Row className="center-md">
              <Col className={`col-sm-12 col-md-6 start-sm ${styles.form}`}>
                {mountBodyForm(components, request)}
              </Col>
            </Row>
          </>
        )}
      </div>

      <Toast />
    </>
  );
}
